import { FC, ReactNode, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import {
	TPgAirwallexAccount,
	TPgApplePayDomainsSyncResult,
	TPgCredentialsCheckStatus,
	TPgWebhookSyncStatus,
} from 'services/api/paymentGateway/types';
import { getPgAirwallexAccountsRequest } from 'redux/reducers/pgSettings/reducer';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { notificationContainer } from 'utils/notificationContainer';
import { PgPanel } from '../../common/PgPanel';
import { usePgCall } from '../../common/usePgCall';
import { formatDateTime } from '../../utils';

type TChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

const WEBHOOK_STATUS: Record<TPgWebhookSyncStatus, { label: string; color: TChipColor }> = {
	never: { label: 'Never synced', color: 'default' },
	ok: { label: 'OK', color: 'success' },
	partial: { label: 'Partial', color: 'warning' },
	manual_required: { label: 'Manual setup required', color: 'warning' },
	error: { label: 'Error', color: 'error' },
};

const CREDENTIALS_STATUS: Record<TPgCredentialsCheckStatus, { label: string; color: TChipColor }> =
	{
		ok: { label: 'Key accepted', color: 'success' },
		rejected: { label: 'Key rejected', color: 'error' },
		error: { label: 'Check failed', color: 'error' },
		not_configured: { label: 'Key not configured', color: 'warning' },
		unknown: { label: 'Not checked yet', color: 'default' },
	};

const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
	<Box sx={{ mb: 1 }}>
		<Typography variant="caption" color="text.secondary" component="div">
			{label}
		</Typography>
		<Box sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{children}</Box>
	</Box>
);

type TAirwallexAccountCardProps = {
	account: TPgAirwallexAccount;
	onEdit: (account: TPgAirwallexAccount) => void;
};

/**
 * One Airwallex account: identity, API key check, webhook subscription and Apple Pay domains
 * (03 §4.4; secrets are never returned by the gateway).
 */
export const AirwallexAccountCard: FC<TAirwallexAccountCardProps> = ({ account, onEdit }) => {
	const dispatch = useDispatch();
	const [registerConfirmOpen, setRegisterConfirmOpen] = useState(false);
	const [applePayResult, setApplePayResult] = useState<TPgApplePayDomainsSyncResult | null>(null);

	const reloadAccounts = useCallback(() => dispatch(getPgAirwallexAccountsRequest()), [dispatch]);

	const credentialsCall = usePgCall(api.paymentGateway.checkAirwallexAccountCredentials, {
		fallbackError: 'Failed to check the API key',
	});
	const webhooksCall = usePgCall(api.paymentGateway.syncAirwallexAccountWebhooks, {
		fallbackError: 'Failed to synchronize the webhook subscription',
	});
	const applePayCall = usePgCall(api.paymentGateway.syncAirwallexAccountApplePayDomains, {
		fallbackError: 'Failed to synchronize Apple Pay domains',
	});

	const handleCheckCredentials = async () => {
		const result = await credentialsCall.run(account.id);
		if (!result.ok) return;
		const status = CREDENTIALS_STATUS[result.data.status] ?? CREDENTIALS_STATUS.unknown;
		notificationContainer(
			`${account.code}: ${status.label}${result.data.error ? ` — ${result.data.error}` : ''}`,
			result.data.status === 'ok' ? 'success' : 'warning',
		);
		reloadAccounts();
	};

	const handleSyncWebhooks = async () => {
		const result = await webhooksCall.run(account.id);
		if (!result.ok) return;
		const status = WEBHOOK_STATUS[result.data.status] ?? WEBHOOK_STATUS.never;
		notificationContainer(
			`${account.code}: webhook subscription — ${status.label}`,
			result.data.status === 'ok' ? 'success' : 'warning',
		);
		reloadAccounts();
	};

	const handleApplePay = async (register: boolean) => {
		setRegisterConfirmOpen(false);
		const result = await applePayCall.run({ id: account.id, body: { register } });
		if (!result.ok) return;
		setApplePayResult(result.data);
		reloadAccounts();
	};

	const webhookStatus = WEBHOOK_STATUS[account.webhook.status] ?? WEBHOOK_STATUS.never;
	const credentialsStatus =
		CREDENTIALS_STATUS[account.credentialsCheck.status] ?? CREDENTIALS_STATUS.unknown;

	return (
		<PgPanel
			title={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
					<Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
						{account.displayName} ({account.code})
					</Typography>
					<Chip
						size="small"
						label={account.environment}
						color={account.environment === 'prod' ? 'primary' : 'default'}
					/>
					{account.isDefault && (
						<Chip size="small" label="Default" color="primary" variant="outlined" />
					)}
					<Chip
						size="small"
						label={account.isActive ? 'Active' : 'Inactive'}
						color={account.isActive ? 'success' : 'default'}
						variant="outlined"
					/>
				</Box>
			}
			actions={
				<Button size="small" variant="contained" onClick={() => onEdit(account)}>
					Edit
				</Button>
			}
		>
			<Grid container spacing={2}>
				<Grid item xs={12} md={4}>
					<Field label="Airwallex account id">{account.awxAccountId ?? '—'}</Field>
					<Field label="Merchant country">{account.merchantCountryCode}</Field>
					<Field label="API key">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
							<Chip
								size="small"
								label={account.credentialsConfigured ? 'Configured' : 'Not configured'}
								color={account.credentialsConfigured ? 'success' : 'warning'}
							/>
							<Chip size="small" label={credentialsStatus.label} color={credentialsStatus.color} />
						</Box>
						<Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
							Last check: {formatDateTime(account.credentialsCheck.checkedAt)}
						</Typography>
						{account.credentialsCheck.error && (
							<Typography variant="caption" color="error" component="div">
								{account.credentialsCheck.error}
							</Typography>
						)}
					</Field>
					<Button
						size="small"
						variant="outlined"
						onClick={handleCheckCredentials}
						disabled={credentialsCall.loading}
						startIcon={credentialsCall.loading ? <CircularProgress size={14} /> : undefined}
					>
						Check API key
					</Button>
				</Grid>
				<Grid item xs={12} md={4}>
					<Field label="Webhook subscription">
						<Chip size="small" label={webhookStatus.label} color={webhookStatus.color} />
					</Field>
					<Field label="URL">{account.webhook.url ?? '—'}</Field>
					<Field label="API version">{account.webhook.version ?? '—'}</Field>
					<Field label="Events">
						{account.webhook.events.length ? account.webhook.events.join(', ') : '—'}
					</Field>
					<Field label="Synced at">{formatDateTime(account.webhook.syncedAt)}</Field>
					{account.webhook.error && (
						<Alert severity="warning" sx={{ mb: 1, py: 0 }}>
							{account.webhook.error}
						</Alert>
					)}
					<Button
						size="small"
						variant="outlined"
						onClick={handleSyncWebhooks}
						disabled={webhooksCall.loading}
						startIcon={webhooksCall.loading ? <CircularProgress size={14} /> : undefined}
					>
						Sync webhooks
					</Button>
				</Grid>
				<Grid item xs={12} md={4}>
					<Field label="Apple Pay domains (registered at Airwallex)">
						{account.applePayDomains.length ? account.applePayDomains.join(', ') : '—'}
					</Field>
					<Field label="Checked at">{formatDateTime(account.applePaySyncedAt)}</Field>
					{applePayResult && (
						<Alert
							severity={
								applePayResult.error
									? 'error'
									: applePayResult.missing.length
										? 'warning'
										: 'success'
							}
							sx={{ mb: 1, py: 0 }}
						>
							{applePayResult.error ? (
								applePayResult.error
							) : (
								<>
									Registered: {applePayResult.registered.join(', ') || '—'}
									<br />
									Missing: {applePayResult.missing.join(', ') || '—'}
								</>
							)}
						</Alert>
					)}
					<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
						<Button
							size="small"
							variant="outlined"
							onClick={() => handleApplePay(false)}
							disabled={applePayCall.loading}
							startIcon={applePayCall.loading ? <CircularProgress size={14} /> : undefined}
						>
							Check domains
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={() => setRegisterConfirmOpen(true)}
							disabled={applePayCall.loading}
						>
							Register missing
						</Button>
					</Box>
					<Divider sx={{ my: 1 }} />
					<Typography variant="caption" color="text.secondary">
						The domain association file must already be served by the gateway; the domains to
						register come from the gateway configuration (ApplePay:Domains).
					</Typography>
				</Grid>
			</Grid>
			<ConfirmationDialog
				open={registerConfirmOpen}
				setClose={() => setRegisterConfirmOpen(false)}
				handleConfirm={() => handleApplePay(true)}
				title="Register Apple Pay domains"
				text={`Register the configured domains that are missing at Airwallex for account <b>${account.code}</b>?`}
			/>
		</PgPanel>
	);
};
